<template>
  <div
    class="group relative flex h-full flex-col"
    :class="[$style.cardContainer]"
    data-spec="product-card-content"
  >
    <div class="p-16" data-spec="main">
      <div
        v-if="withMarketingTags"
        class="flex mb-8"
        :class="{
          [$style.marketingTag]: options.format !== 'compact',
        }"
      >
        <MarketingTag
          v-if="product.marketingTags?.[0]"
          :tag="product.marketingTags[0]"
        />
      </div>
      <div class="flex">
        <div
          class="flex max-w-full grow content-start justify-center gap-8"
          :class="[
            $style.cardWrapper,
            {
              'flex-row items-start': options.format === 'compact',
              'flex-col': options.format !== 'compact',
            },
          ]"
          data-spec="image+main-information"
        >
          <div
            class="flex flex-col items-center justify-center gap-4"
            data-spec="image+colors"
          >
            <RevIllustration
              alt=""
              data-test="product-card-image"
              :height="imageSize"
              :sizes="`${imageSize}px`"
              :src="product.imageSrc"
              :width="imageSize"
            />

            <ColorSwatches
              v-if="product.colorSwatches"
              :colors="product.colorSwatches"
              data-test="product-color-swatches"
            />
          </div>

          <div
            class="flex min-w-0 grow flex-col items-start gap-6"
            data-spec="product-info"
          >
            <div class="flex flex-col gap-2 max-w-full" data-spec="titles">
              <ProductCardDeal
                v-if="dealContent"
                class="relative mb-6"
                :deal-content
              />
              <h2>
                <RevButtonBase
                  ref="link"
                  class="after:focus-visible-outline-default-hi after:rounded-lg after:absolute after:inset-0 after:bg-transparent focus-visible:outline-none"
                  :to="product.url"
                  @click="emit('link-click')"
                >
                  <span
                    class="body-1-bold line-clamp-2"
                    data-test="product-title"
                  >
                    {{ product.title }}
                  </span>
                  <span
                    v-if="enrichedDescription"
                    class="text-static-default-low body-2"
                    :class="
                      options.extendedDescription
                        ? 'line-clamp-3'
                        : 'line-clamp-2'
                    "
                    data-test="product-description"
                  >
                    {{ enrichedDescription }}
                  </span>
                </RevButtonBase>
              </h2>

              <span v-if="product.warranty && withWarranty" class="body-2">
                {{
                  i18n(translations.productCardWarranty, {
                    warranty: product.warranty,
                  })
                }}
                {{ i18n(translations.months) }}
              </span>
            </div>

            <ProductCardRatings
              v-if="
                product.reviewRating?.count && product.reviewRating?.average
              "
              :count="product.reviewRating.count"
              data-spec="rating"
              :score="product.reviewRating.average"
            />

            <div
              v-if="product.specifications && product.specifications.length > 0"
            >
              <ProductSpecs :specifications="product.specifications" />
            </div>
            <div class="flex w-full items-start justify-between gap-x-6">
              <div v-if="product.price" data-spec="price-information">
                <div
                  v-if="options.withStartingFrom"
                  class="text-static-default-low caption"
                  data-test="product-starting-from"
                >
                  {{ i18n(translations.startingFrom) }}
                </div>
                <ProductPrice
                  :discounted-price="
                    product.discountedPrice &&
                    i18n.price(product.discountedPrice)
                  "
                  :has-deal="!!dealContent"
                  :price="i18n.price(product.price)"
                  :price-new="product.priceNew && i18n.price(product.priceNew)"
                />
              </div>

              <AddToCartButton
                v-if="shouldShowAddToCart && options.format === 'compact'"
                :id="product.listingId"
                :error-mode="options.addToCartErrorMode"
                :format="options.format"
                :product
                :success-mode="options.addToCartSuccessMode"
                :trackingData="
                  productToTrackingData(product.rawProduct, {
                    list: options.list,
                    position: options.position,
                  })
                "
                @add-to-cart="addToCart"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <ProductComparisonCheckbox
      v-if="comparison?.isComparisonEnabled"
      class="absolute bottom-8 right-8 lg:bottom-auto lg:top-8"
      :is-checked="isProductCompared"
      :is-comparison-add-disabled="
        comparison.isComparisonAddProductDisabled ?? false
      "
      :is-responsive-comparison-enabled="
        comparison.isResponsiveComparisonEnabled ?? false
      "
      @change="emit('toggle-comparison-product', product.listingId)"
    />
    <div
      v-if="shouldShowAddToCart && options.format !== 'compact'"
      class="mt-auto p-16 pt-0"
    >
      <AddToCartButton
        :id="product.listingId"
        :error-mode="options.addToCartErrorMode"
        :format="options.format"
        :product
        :success-mode="options.addToCartSuccessMode"
        :text="i18n(translations.addToCart)"
        :trackingData="
          productToTrackingData(product.rawProduct, {
            list: options.list,
            position: options.position,
          })
        "
        @add-to-cart="addToCart"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevIllustration } from '@ds/components/Illustration'

import { useProductCardDeal } from '../../composables/useProductCardDeal'
import { productToTrackingData } from '../../composables/useProductTracking'
import type { ComparisonProduct } from '../../models/product'
import AddToCartButton from '../AddToCartButton/AddToCartButton.vue'
import ColorSwatches from '../ColorSwatches.vue'
import ProductCardDeal from '../ProductCardDeal.vue'
import ProductCardRatings from '../ProductCardRatings.vue'
import ProductComparisonCheckbox from '../ProductComparison/components/ProductComparisonCheckbox.vue'
import ProductPrice from '../ProductPrice.vue'
import ProductSpecs from '../ProductSpecs.vue'

import { BATTERY_SPECIAL_OFFER } from './ProductCard.constants'
import translations from './ProductCard.translations'
import type { ProductCardProduct } from './ProductCard.types'
import MarketingTag from './components/MarketingTag.vue'

const {
  comparison,
  product,
  options = {},
} = defineProps<{
  product: ProductCardProduct
  options?: {
    withAddToCart?: boolean
    list?: string
    position?: number
    withStartingFrom?: boolean
    extendedDescription?: boolean
    format?: 'default' | 'compact'
    withMarketingTags?: boolean
    withDeal?: boolean
    withWarranty?: boolean
    forceHideAddToCart?: boolean
    addToCartSuccessMode?: 'popover' | 'silent'
    addToCartErrorMode?: 'popover' | 'inline'
  }
  comparison?: {
    isComparisonEnabled?: boolean
    isComparisonAddProductDisabled?: boolean
    isResponsiveComparisonEnabled?: boolean
    comparisonProducts?: ComparisonProduct[]
  }
}>()

const {
  withMarketingTags = true,
  withDeal = true,
  withWarranty = true,
} = options

const emit = defineEmits([
  'add-to-cart',
  'toggle-comparison-product',
  'link-click',
])

const i18n = useI18n()
const { dealContent } = useProductCardDeal(
  product.deal,
  product.granularity,
  withDeal,
)

function addToCart({
  status,
  error,
}: {
  status: string | undefined
  error?: unknown
}) {
  emit('add-to-cart', { status, error })
}

const imageSize = computed(() => {
  if (options.format === 'compact') return 80

  return product.granularity === 'backbox' ? 224 : 128
})

const isProductCompared = computed(() => {
  return (
    comparison?.comparisonProducts?.some(
      (p) => p.listingId === product.listingId,
    ) || false
  )
})

const shouldShowAddToCart = computed(() => {
  if (options.forceHideAddToCart) return false

  return options.withAddToCart || product.granularity === 'backbox'
})

// TODO [SR-5653]: The backend will eventually return attributes as a proper array, which will
// remove the need for the ugly split(' • ') on the description string.
const enrichedDescription = computed(() => {
  if (product.granularity === 'parent') return undefined

  return [
    product.granularity === 'backbox' && product.grade?.name,
    ...(product.description?.split(' • ') ?? []),
    product.granularity === 'backbox' &&
      product.specialOfferType === BATTERY_SPECIAL_OFFER &&
      i18n(translations.specialOfferType7),
  ]
    .filter(Boolean)
    .join(' • ')
})
</script>

<style module>
.cardContainer {
  container-type: inline-size;
  container-name: product-card;
}

@container product-card (width <= 326px) {
  .marketingTag {
    min-height: 16px;
  }
}

@container product-card (width > 326px) {
  .cardWrapper {
    flex-direction: row;
  }
}
</style>
